import React, { useState, useMemo } from "react";
import { FileText } from "lucide-react";
import API from "../../services/api";
import { useCanEdit } from "../../services/permissions";
import { downloadWithAuth, deliveryOf, jobDeliveryDate, sortRunsForPicker, runLabel } from "./util";

// Fields the operator can correct on an unassigned stop; keys are exactly
// what PATCH /jobs/{id}/stop-data accepts.
const EDIT_FIELDS = [
  ["Invoice / S.O. #", "order_or_so_number"],
  ["Customer", "customer_name"],
  ["Address Line", "address_line"],
  ["Recipient Phone", "recipient_phone_number"],
  ["Warehouse Name", "warehouse_name"],
  ["Store", "store"],
  ["Pickup From", "pickup_from"],
  ["Instructions", "instructions"],
];

// Assign-to-run control. Runs are ordered by DELIVERY day (not the day the
// sheet arrived) and the run delivering on the stop's own day is
// pre-selected, so the common case is one click on "Assign".
export function RunPicker({ runs, matchDate, onAssign, busy }) {
  const sorted = useMemo(() => sortRunsForPicker(runs), [runs]);
  const match = matchDate ? sorted.find((r) => deliveryOf(r) === matchDate) : null;
  // an explicit choice wins over the pre-selection
  const [choice, setChoice] = useState(null);
  const value = choice != null ? choice : (match ? String(match.id) : "");
  return (
    <div className="flex items-center gap-1">
      <select
        className={`bg-slate-900 border rounded px-2 py-1 text-slate-300 max-w-[260px] ${value && match && value === String(match.id) ? "border-emerald-500/50" : "border-slate-700"}`}
        value={value}
        onChange={(e) => setChoice(e.target.value)}
        title={match ? `Pre-selected: delivers ${matchDate}` : "Choose the run this stop goes on"}
      >
        <option value="">Assign to run…</option>
        {sorted.map((r) => (
          <option key={r.id} value={String(r.id)}>
            {runLabel(r)}{matchDate && deliveryOf(r) === matchDate ? " ✓" : ""}
          </option>
        ))}
      </select>
      <button
        onClick={() => value && onAssign(value)}
        disabled={!value || busy}
        className="border border-blue-500/40 text-blue-300 hover:bg-blue-600 hover:text-white rounded px-2 py-1 font-medium disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-blue-300">
        {busy ? "…" : "Assign"}
      </button>
    </div>
  );
}

// One unassigned stop: the same row serves the unassigned pool and the
// manual-import tab, so an emailed order and an uploaded one get the same
// Docs / Edit / Assign controls and land on a run the same way.
export default function PoolRow({ job, runs, hintDate, onChanged, onError, onNotice }) {
  const canEdit = useCanEdit();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);

  const openEditor = () => {
    setDraft({
      order_or_so_number: job.so_number || job.invoiceNo || "",
      customer_name: job.customer_name || "",
      address_line: job.address_line || job.destination_address || "",
      recipient_phone_number: job.recipient_phone || "",
      warehouse_name: job.warehouse_name || "",
      store: job.store_name || "",
      pickup_from: job.pickup_from || job.comingFrom || "",
      instructions: job.run_instructions || "",
    });
    setEditing(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await API.patch(`/jobs/${encodeURIComponent(job.id)}/stop-data`, draft);
      setEditing(false);
      onChanged && onChanged("edited", job.id);
    } catch (err) {
      onError && onError(err);
    } finally {
      setBusy(false);
    }
  };

  const assign = async (runId) => {
    setBusy(true);
    try {
      await API.post(`/runs/${runId}/assign-job/${encodeURIComponent(job.id)}`);
      const r = (runs || []).find((x) => String(x.id) === String(runId));
      onNotice && onNotice(`${job.invoiceNo || job.order_number || "Stop"} assigned to ${r ? runLabel(r) : `run #${runId}`}`, r ? r.id : Number(runId));
      onChanged && onChanged("assigned", job.id, runId);
    } catch (err) {
      onError && onError(err);
    } finally {
      setBusy(false);
    }
  };

  const docs = async () => {
    try {
      await downloadWithAuth(`/jobs/${encodeURIComponent(job.id)}/source.pdf`, `${job.invoiceNo || job.order_number || job.id}-source.pdf`);
    } catch (err) {
      onError && onError(err);
    }
  };

  const ownDate = jobDeliveryDate(job);
  const matchDate = ownDate || hintDate || null;

  return (
    <div className="bg-slate-900/60 rounded-lg border border-slate-700 text-xs">
      <div className="flex flex-wrap items-center gap-3 px-3 py-2">
        <span className="text-slate-500 w-16 shrink-0" title="Received">{(job.email_date_iso || job.createdAt || "").slice(5, 10) || "—"}</span>
        <span className="font-mono text-slate-400">{job.invoiceNo || job.order_number || job.id}</span>
        <span className="text-slate-300 flex-1 min-w-[160px] truncate" title={job.sourceEmailSubject || job.source_filename}>
          {job.sourceEmailSubject || job.source_filename || "(no subject)"}
        </span>
        <span className="text-slate-400 truncate max-w-[240px]">
          {job.comingFrom || job.pickup_from || "?"} → {job.customer_name ? `${job.customer_name}, ` : ""}{job.address_line || job.destination_address || job.destination || "customer"}
        </span>
        {ownDate && (
          <span className="text-emerald-400/80 whitespace-nowrap" title="Delivery date on the order">deliver {ownDate.slice(5)}</span>
        )}
        <button onClick={docs} title="Download this stop's source documents"
          className="border border-slate-700 rounded px-2 py-1 text-slate-400 hover:text-indigo-400 flex items-center gap-1">
          <FileText size={12} /> Docs
        </button>
        {/* Correcting a stop and putting it on a run are both writes. Docs
            stays for everyone - reading the source document is not editing. */}
        {canEdit && (
          <button
            onClick={() => (editing ? setEditing(false) : openEditor())}
            className={`border rounded px-2 py-1 ${editing ? "border-blue-500 text-blue-400" : "border-slate-700 text-slate-400 hover:text-white"}`}>
            {editing ? "Close" : "Edit"}
          </button>
        )}
        {canEdit ? (
          <RunPicker runs={runs} matchDate={matchDate} onAssign={assign} busy={busy} />
        ) : (
          <span className="text-slate-500 whitespace-nowrap">Unassigned</span>
        )}
      </div>
      {canEdit && editing && (
        <div className="border-t border-slate-700/60 px-3 py-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          {EDIT_FIELDS.map(([label, key]) => (
            <label key={key} className={key === "instructions" ? "col-span-2" : ""}>
              <span className="block text-[10px] uppercase tracking-wide text-slate-500 mb-1">{label}</span>
              <input
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-slate-200 outline-none focus:border-blue-500"
                value={draft[key] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              />
            </label>
          ))}
          <div className="flex items-end justify-end gap-2 col-span-2 md:col-span-1">
            <button onClick={() => setEditing(false)}
              className="px-3 py-1.5 rounded text-slate-300 hover:bg-slate-800">Cancel</button>
            <button onClick={save} disabled={busy}
              className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-50">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
